import React, { useState, useEffect, useRef, useCallback } from "react";
import { CalculatorSliderField } from "@/components/design-system/Calculator/CalculatorSliderField";
import { CalculatorDonutChart, type DonutSegment } from "@/components/design-system/Calculator/CalculatorDonutChart";
import { CalculatorStatCard } from "@/components/design-system/Calculator/CalculatorStatCard";
import { CalculatorFooter } from "@/components/design-system/Calculator/CalculatorFooter";
import styles from "@/components/design-system/Calculator/Calculator.module.css";
import {
  type EmployerCalculatorInputs,
  DEFAULT_INPUTS,
  formatDollar,
  formatCompactDollar,
  formatPercent,
  formatCount,
  calcResults,
} from "./employerCalculator.utils";

type EmployerCalculatorProps = {
  onCtaClick?: () => void;
};

export const EmployerCalculator: React.FC<EmployerCalculatorProps> = ({ onCtaClick }) => {
  const [inputs, setInputs] = useState<EmployerCalculatorInputs>(DEFAULT_INPUTS);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [displayTotal, setDisplayTotal] = useState(0);
  const animationFrameRef = useRef<number | null>(null);
  const previousTotalRef = useRef(0);

  const results = calcResults(inputs);

  useEffect(() => {
    const animateFrom = previousTotalRef.current;
    const animateTo = results.total;
    previousTotalRef.current = animateTo;
    if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    const start = performance.now();
    const duration = 320;
    const tick = (now: number) => {
      const progress = Math.min((now - start) / duration, 1);
      const easedProgress = 1 - Math.pow(1 - progress, 3);
      setDisplayTotal(animateFrom + (animateTo - animateFrom) * easedProgress);
      if (progress < 1) animationFrameRef.current = requestAnimationFrame(tick);
    };
    animationFrameRef.current = requestAnimationFrame(tick);
    return () => { if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current); };
  }, [results.total]);

  const handleChange = useCallback((field: keyof EmployerCalculatorInputs, value: number) => {
    setInputs((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleReset = () => setInputs(DEFAULT_INPUTS);

  const benchmarkSalaries = inputs.salary > 0 ? Math.round(results.total / inputs.salary) : 0;

  const donutSegments: DonutSegment[] = [
    { label: "Productivity Loss",    value: formatDollar(results.prodLoss),  color: "#D4C4D3", rawValue: results.prodLoss  },
    { label: "Stress Turnover",      value: formatDollar(results.turnCost),  color: "#bfdbfe", rawValue: results.turnCost  },
    { label: "Absenteeism",          value: formatDollar(results.absCost),   color: "#946B94", rawValue: results.absCost   },
    { label: "Healthcare + Vacancy", value: formatDollar(results.otherCost), color: "#e5e5e5", rawValue: results.otherCost },
  ];

  return (
    <div
      className="bg-primary-500 rounded-7 relative mx-auto w-full max-w-235 overflow-hidden font-sans"
      style={{ boxShadow: "0 4px 40px 0 #361637, 0 32px 80px rgba(45,18,48,0.45), 0 8px 24px rgba(0,0,0,0.15)" }}
    >
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: "radial-gradient(ellipse 75% 60% at -5% 105%, rgba(255,255,255,0.12) 0%, transparent 55%), radial-gradient(ellipse 55% 45% at 105% -5%, rgba(45,18,48,0.6) 0%, transparent 55%)" }}
      />

      <div className="relative z-1 p-[28px_28px_22px]">
        <div className="mb-5.5 flex flex-wrap items-center justify-between gap-3">
          <img src="/logo.png" alt="Bravo" className="h-8 w-auto brightness-0 invert" />
        </div>

        <div className={styles.calcGrid}>
          {/* FORM PANEL */}
          <div className="rounded-[18px] border border-white/60 bg-[#faf6fb] p-[22px_20px_24px]">
            <div className="text-primary-500 text-[11px] mb-4 text-center font-extrabold tracking-[0.08em] uppercase">
              The Real Cost of Financial Stress
            </div>

            <div className={`${styles.sectionHeading} ${styles.sectionHeadingFirst}`}>Core Inputs</div>

            <CalculatorSliderField label="Total employees" tooltip="Total headcount. Approx. 60% are likely financially stressed (PwC Employee Financial Wellness Survey)." value={inputs.employees} min={50} max={10000} step={50} minLabel="50" maxLabel="10,000" onChange={(val) => handleChange("employees", val)} />
            <CalculatorSliderField label="Average salary" tooltip="U.S. median full-time wage ~$65,470 (BLS, 2024)." value={inputs.salary} min={30000} max={200000} step={1000} prefix="$" minLabel="$30k" maxLabel="$200k" onChange={(val) => handleChange("salary", val)} />
            <CalculatorSliderField label="Turnover rate" tooltip="Annual turnover %. U.S. avg ~47% (BLS JOLTS, Aug 2025); white-collar typically 10–20%." value={inputs.turnover} min={1} max={60} step={1} suffix="%" minLabel="1%" maxLabel="60%" onChange={(val) => handleChange("turnover", val)} />
            <CalculatorSliderField label="Cost to replace employee" tooltip="SHRM estimates 50–200% of annual salary, covering recruiting, onboarding, and productivity gap." value={inputs.replace} min={5000} max={150000} step={500} prefix="$" minLabel="$5k" maxLabel="$150k" onChange={(val) => handleChange("replace", val)} />

            <button
              onClick={() => setAdvancedOpen((isOpen) => !isOpen)}
              className="mt-3.5 flex w-full cursor-pointer items-center gap-2 rounded-lg border border-[#e0d0e2] bg-[#f5f0f6] px-3 py-2.25 transition-colors duration-150 select-none hover:bg-[#eddcee]"
            >
              <span className="text-primary-500 text-[11px] flex-1 text-left font-extrabold">Advanced factors</span>
              <span className="flex h-3.5 w-3.5 shrink-0 items-center justify-center transition-transform duration-300 ease-in-out" style={{ transform: advancedOpen ? "rotate(180deg)" : "rotate(0deg)" }}>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M2 4L6 8L10 4" stroke="#5B245C" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
            </button>

            <div style={{ maxHeight: advancedOpen ? 900 : 0, overflow: "hidden", transition: "max-height 0.4s cubic-bezier(0.4,0,0.2,1)" }}>
              <div className={styles.sectionHeading}>Absenteeism</div>
              <CalculatorSliderField label="Extra sick days/stressed employee/yr" tooltip="Stressed employees take 2–4 more unplanned absences/yr (Cigna; PwC). Applied to the 57% actively showing impact." value={inputs.sickdays} min={0} max={15} step={1} minLabel="0 days" maxLabel="15 days" onChange={(val) => handleChange("sickdays", val)} />
              <div className={styles.sectionHeading}>Healthcare</div>
              <CalculatorSliderField label="Extra healthcare cost/stressed employee" tooltip="Stress-related claims add ~$600–$800/yr per affected employee (APA; HBR, 2015)." value={inputs.healthcare} min={0} max={2000} step={50} prefix="$" minLabel="$0" maxLabel="$2,000" onChange={(val) => handleChange("healthcare", val)} />
              <div className={styles.sectionHeading}>Presenteeism</div>
              <CalculatorSliderField label="Output loss while present but stressed" tooltip="15–20% reduced cognitive performance at work (Mullainathan & Shafir, Scarcity, 2013; Gallup)." value={inputs.presenteeism} min={5} max={40} step={1} suffix="%" minLabel="5%" maxLabel="40%" onChange={(val) => handleChange("presenteeism", val)} />
              <div className={styles.sectionHeading}>Vacancy & Onboarding</div>
              <CalculatorSliderField label="Weeks role vacant after departure" tooltip="Avg time-to-fill 36–42 days; full productivity after 8–12 weeks (SHRM; HBR, 2022)." value={inputs.vacancyweeks} min={1} max={26} step={1} minLabel="1 wk" maxLabel="26 wks" onChange={(val) => handleChange("vacancyweeks", val)} />
            </div>

            <button
              onClick={handleReset}
              className="rounded-[10px] mt-4 block w-full cursor-pointer border-none p-3 text-sm font-extrabold text-white transition-[opacity,transform] duration-200 hover:-translate-y-px hover:opacity-88"
              style={{ background: "linear-gradient(37.77deg, #B662A6 -20%, #5B245C 70%)" }}
            >
              Reset
            </button>
          </div>

          {/* RESULTS PANEL */}
          <div className="rounded-[18px] border border-white/18 bg-white/7 p-[22px_18px]">
            <div className="text-[13px] mb-4 text-center font-extrabold tracking-[0.08em] text-white/75 uppercase">Results</div>

            <CalculatorDonutChart
              segments={donutSegments}
              centerValue={formatCompactDollar(displayTotal)}
              benchmarkText={<>equiv. <strong className="text-white/60">{benchmarkSalaries.toLocaleString("en-US")}</strong> salaries p.a.</>}
            />

            <hr className="my-3.25 border-t border-none border-white/12" />

            <div className="grid grid-cols-2 gap-1.75">
              <CalculatorStatCard label="Cost per employee"  value={formatDollar(results.costPerEmp)}      subtext="annually, all staff"          highlight />
              <CalculatorStatCard label="% of total payroll" value={formatPercent(results.payrollPct)}      subtext="hidden cost on books"                  />
              <CalculatorStatCard label="Stressed employees" value={formatCount(results.stressed)}          subtext="est. 60% of workforce"                 />
              <CalculatorStatCard label="Potential saving"   value={formatCompactDollar(results.potentialSaving)} subtext="with 30% stress reduction"    />
            </div>
          </div>
        </div>
      </div>

      <CalculatorFooter
        sources={[
          <><em>PwC Employee Financial Wellness Survey</em>: 60% stressed; 57% lose ≥3 hrs/wk productivity. <a href="https://pwc.com" target="_blank" rel="noreferrer" className="text-white/85">pwc.com</a></>,
          <>UMass Lowell / CPH-NEW — ~40% of voluntary turnover attributable to financial stress. <a href="https://uml.edu" target="_blank" rel="noreferrer" className="text-white/85">uml.edu</a></>,
          <>SHRM — Replacement costs 50%–200% of salary. <a href="https://shrm.org" target="_blank" rel="noreferrer" className="text-white/85">shrm.org</a> · APA / HBR — $600–$800 extra healthcare/yr per stressed employee. · Vanguard <em>How America Saves 2025</em> — record 4.8% hardship withdrawals. <a href="https://vanguard.com" target="_blank" rel="noreferrer" className="text-white/85">vanguard.com</a> · BLS JOLTS Aug 2025. <a href="https://bls.gov" target="_blank" rel="noreferrer" className="text-white/85">bls.gov</a></>,
        ]}
        cta={
          <span className={styles.footerCtaLabel} onClick={onCtaClick}>
            See how Bravo can reduce this cost →
          </span>
        }
      />
    </div>
  );
};
